import React, { useState, useEffect, createContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import Container from './Menu/Container';
import './App.css';
import { DialogContext } from './const/consts';
import { serverPHP, debug_mode } from './const/consts';
import ErrorHandler from './composants/ErrorHandler';

// Nouveau contexte pour l'état de chargement
export const LoadingContext = createContext(false);

const App = ({ goTo = null }) => {
  const [user, setUser] = useState({
    id: null,
    name: '',
    image: null,
    color: '',
    role: 0,
    currentsession: null,
    isready: false
  });
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true); // NOUVEAU : État pour l'initialisation globale
  const [oldUser, setOldUser] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  const [creatingSession, setCreatingSession] = useState(false);
  const [joinSession, setJoinSession] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  const initialLoad = useRef(true);

  // Ce useEffect gère la navigation et l'initialisation de l'utilisateur.
  useEffect(() => {
    if (debug_mode) console.log(`[App.js Effect] Navigation changed. goTo: "${goTo}", id: "${id}"`);

    const initializeUser = async () => {
      setInitializing(true); // NOUVEAU : Marquer le début de l'initialisation
      
      const userId = localStorage.getItem('userId');

      if (!userId) {
        if (debug_mode) console.log("[App.js Effect] No userId found in localStorage. Creating new user.");
        await createNewUser();
      } else {
        if (debug_mode) console.log(`[App.js Effect] Found userId ${userId}. Checking existence.`);
        await checkUserExists(userId);
      }
      
      setInitializing(false); // NOUVEAU : Marquer la fin de l'initialisation
    };

    initializeUser();

    // Cette partie gère l'état de l'UI (panneaux de création/jointure)
    if (goTo === "nouvellesession") {
      setCreatingSession(true);
      setJoinSession(false);
    } else if (goTo === "listesessions") {
      setJoinSession(true);
      setCreatingSession(false);
    } else {
      setCreatingSession(false);
      setJoinSession(false);
    }

  }, [goTo, id]);

  // Ce useEffect met à jour la BDD quand l'état `user` change.
  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }

    if (user && oldUser && user.id !== null && JSON.stringify(user) !== JSON.stringify(oldUser)) {
      if (debug_mode) console.log("[App.js User Sync] User state changed, updating database.", { new: user, old: oldUser });
      updateUserInDatabase(user);
    }
    setOldUser(user);
  }, [user]);

  const checkUserExists = async (userId) => {
    const attemptCheckUserExists = async () => {
      try {
        setError(null);
        // IMPORTANT : Ne pas setLoading(false) prématurément
        
        if (debug_mode) console.log(`[checkUserExists] Fetching user ${userId}...`);
        
        const timeoutId = setTimeout(() => {
          throw new Error('TIMEOUT');
        }, 10000);

        const response = await fetch(serverPHP + `checkUser.php?id=${userId}`, { method: 'GET' });
        clearTimeout(timeoutId);

        if (!response.ok) {
          if (response.status === 404) throw new Error('USER_NOT_FOUND');
          throw new Error(`HTTP_ERROR_${response.status}`);
        }

        const data = await response.json();
        
        if (data.exists) {
          if (debug_mode) console.log("[checkUserExists] User exists. Raw data from DB:", data.user);
          
          let updatedUser = { ...data.user };

          // Force l'état de la session en fonction de l'URL
          if (goTo === "session" && id) {
            if (debug_mode) console.log(`[checkUserExists] Forcing session to "${id}" based on URL.`);
            updatedUser.currentsession = id;
          } else if (goTo !== "session") {
            if (debug_mode) console.log("[checkUserExists] Forcing session to null because we are not on a session page.");
            updatedUser.currentsession = null;
          }
          
          if (debug_mode) console.log("[checkUserExists] Final user object to be set:", updatedUser);
          
          initialLoad.current = true;
          setUser(updatedUser);
          setLoading(false); // MODIFICATION : On garde setLoading ici car l'utilisateur est trouvé
          setRetryCount(0);
        } else {
          // Si l'utilisateur n'est pas trouvé dans la BDD, on en crée un nouveau
          if (debug_mode) console.log("[checkUserExists] User not found in DB. Creating a new one.");
          localStorage.removeItem('userId');
          await createNewUser();
        }
        
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'existence de l\'utilisateur :', error);
        
        if (error.message === 'USER_NOT_FOUND') {
          createNewUser();
          return;
        }
        
        setError({
          type: error.message || 'UNKNOWN_ERROR',
          message: 'Erreur lors de la vérification du profil utilisateur.',
          details: error.stack
        });
        setLoading(false);
        setInitializing(false); // NOUVEAU : Arrêter l'initialisation en cas d'erreur
      }
    };
    await attemptCheckUserExists();
  };

  const createNewUser = async () => {
    const attemptCreateNewUser = async () => {
      try {
        setError(null);
        // La session est définie en fonction de l'URL actuelle
        let sessionId = (goTo === "session" && id) ? id : null;
        if (debug_mode) console.log(`[createNewUser] Creating user with initial session: ${sessionId}`);

        const newUser = await createUserInDatabase(sessionId);
        
        if (newUser) {
          if (debug_mode) console.log("[createNewUser] User created successfully in DB:", newUser);
          localStorage.setItem('userId', newUser.id);
          initialLoad.current = true;
          setUser(newUser);
          setLoading(false);
          setRetryCount(0);
        }
        
      } catch (error) {
        console.error('Erreur lors de la création de l\'utilisateur :', error);
        setError({
            type: error.message || 'UNKNOWN_ERROR',
            message: 'Erreur lors de la création du profil utilisateur.',
            details: error.stack
        });
        setLoading(false);
        setInitializing(false); // NOUVEAU : Arrêter l'initialisation en cas d'erreur
      }
    };

    await attemptCreateNewUser();
  };

  const createUserInDatabase = async (sessionId) => {
    const timeoutId = setTimeout(() => {
      throw new Error('TIMEOUT');
    }, 10000);

    try {
      const response = await fetch(serverPHP + 'createUser.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ currentsession: sessionId })
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status >= 500) {
          throw new Error('SERVER_ERROR');
        } else {
          throw new Error(`HTTP_ERROR_${response.status}`);
        }
      }

      const data = await response.json();
      
      if (data.user) {
        return data.user;
      } else {
        throw new Error('Invalid response format');
      }
      
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  const updateUserInDatabase = async (user) => {
    try {
      const response = await fetch(serverPHP + 'updateUser.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(user)
      });

      if (!response.ok) {
        if (response.status >= 500) {
          throw new Error('SERVER_ERROR');
        } else {
          throw new Error(`HTTP_ERROR_${response.status}`);
        }
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Update failed');
      }
      
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'utilisateur :', error);
      if (debug_mode) {
        console.warn('Mise à jour utilisateur échouée:', error.message);
      }
    }
  };

  const handleRetry = () => {
    if (retryCount < maxRetries) {
      setRetryCount(prev => prev + 1);
      setLoading(true);
      setInitializing(true); // NOUVEAU : Réinitialiser l'état d'initialisation
      setError(null);
      
      const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
      setTimeout(() => {
        const userId = localStorage.getItem('userId');
        if (!userId) {
          createNewUser();
        } else {
          checkUserExists(userId);
        }
      }, delay);
    } else {
      localStorage.removeItem('userId');
      setRetryCount(0);
      createNewUser();
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  // Gestion des erreurs
  if (error) {
    return (
      <div className="app-container">
        <div className="main-content">
          <ErrorHandler
            error={error}
            onRetry={retryCount < maxRetries ? handleRetry : null}
            onGoHome={handleGoHome}
            showRetry={retryCount < maxRetries}
            customActions={retryCount >= maxRetries ? [
              { 
                text: '👤 Créer un nouveau profil', 
                action: () => {
                  localStorage.removeItem('userId');
                  setRetryCount(0);
                  createNewUser();
                }, 
                primary: true 
              },
              { text: '🔄 Recharger la page', action: () => window.location.reload(), secondary: true }
            ] : null}
          />
        </div>
      </div>
    );
  }

  // MODIFICATION IMPORTANTE : Pendant l'initialisation, afficher le Container avec un état neutre
  if (loading || initializing) {
    return (
      <LoadingContext.Provider value={true}>
        <DialogContext.Provider value={[dialogVisible, setDialogVisible]}>
          <div className="app-container">
            <Container 
              user={user} 
              setUser={setUser} 
              creatingSession={false} 
              setCreatingSession={setCreatingSession} 
              joinSession={false} 
              setJoinSession={setJoinSession} 
            />
          </div>
        </DialogContext.Provider>
      </LoadingContext.Provider>
    );
  }

  return (
    <LoadingContext.Provider value={false}>
      <DialogContext.Provider value={[dialogVisible, setDialogVisible]}>
        <div className="app-container">
          {user.id !== null ? (
            <Container 
              user={user} 
              setUser={setUser} 
              creatingSession={creatingSession} 
              setCreatingSession={setCreatingSession} 
              joinSession={joinSession} 
              setJoinSession={setJoinSession} 
            />
          ) : (
            <div className="main-content">
              <div className="card text-center">
                <h2 className="card-title">⚠️ Erreur</h2>
                <p>Aucun utilisateur trouvé. Veuillez recharger la page.</p>
                <div className="flex gap-2 justify-center mt-4">
                  <button className="btn" onClick={() => window.location.reload()}>
                    🔄 Recharger
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContext.Provider>
    </LoadingContext.Provider>
  );
}

export default App;